import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getErpBranchDetails, getTenantBrandImageSrcWithFallback } from "../erpClient";
import { getSessionBranchId } from "./sessionBranch";
import { getTenantNameUpper } from "./tenantName";
import { formatDateToDDMMYYYY } from "../pages/const";

const BORDER_COLOR = [140, 29, 26]; // #8c1d1a
const HEADER_BG = [240, 230, 210]; // #f0e6d2
const CREDIT_BG = [232, 245, 234]; // #e8f5ea
const DEBIT_BG = [252, 232, 234]; // #fce8ea
const BALANCE_BG = [232, 244, 247]; // #e8f4f7
const OPENING_BG = [227, 242, 253]; // #e3f2fd

function toEnglishDigits(value) {
  return String(value ?? "").replace(/[૦-૯]/g, (digit) =>
    String("૦૧૨૩૪૫૬૭૮૯".indexOf(digit))
  );
}

function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw.split(/\n/).map((s) => s.trim()).filter(Boolean);
  }
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  return [`${parts.slice(0, 2).join(", ")},`, parts.slice(2).join(", ")];
}

const formatDisplayDate = (dateString) => {
  if (!dateString || dateString === "-") return "-";
  try {
    const str = String(dateString).trim();
    if (str.includes("/")) return str; // already DD/MM/YYYY
    const parts = str.split(" ")[0].split("-");
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      if (parts[2].length === 4) {
        // DD-MM-YYYY
        return `${parts[0]}/${parts[1]}/${parts[2]}`;
      }
    }
    const date = new Date(dateString);
    if (!Number.isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return formatDateToDDMMYYYY(str.split(" ")[0]) || dateString;
  } catch {
    return dateString;
  }
};

function formatIndianCurrency(value) {
  if (value === "" || value == null || value === "-") return "-";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(num)) return "-";
  if (num === 0) return "-";
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `-${formatted}` : formatted;
}

function formatIndianBalance(value) {
  if (value === "" || value == null || value === "-") return "-";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(num)) return "-";
  if (num === 0) return "0.00";
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `-${formatted}` : formatted;
}

function getFormattedGeneratedOn() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
}

function loadImageAsDataUrl(src) {
  return new Promise((resolve) => {
    if (!src || typeof window === "undefined") return resolve(null);
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function renderTopBarCanvas(width) {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  const scale = 3;
  const height = 16;
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);

  ctx.fillStyle = "#8c1d1a";
  ctx.font = "bold 7.5px 'Noto Sans Gujarati', 'Shruti', 'Gujarati Sangam MN', 'Arial Unicode MS', sans-serif";
  ctx.textBaseline = "middle";

  ctx.textAlign = "left";
  ctx.fillText("|| શ્રી પદમાવતી માતાય નમ: ||", 2, height / 2 - 1);

  ctx.textAlign = "center";
  ctx.fillText("|| શ્રી ગણેશાય નમ: ||", width / 2, height / 2 - 1);

  ctx.textAlign = "right";
  ctx.fillText("|| શ્રી બુટભવાની માતાય નમ: ||", width - 2, height / 2 - 1);

  // Bottom dividing line
  ctx.strokeStyle = "#8c1d1a";
  ctx.lineWidth = 0.75;
  ctx.beginPath();
  ctx.moveTo(0, height - 0.5);
  ctx.lineTo(width, height - 0.5);
  ctx.stroke();

  return canvas.toDataURL("image/png");
}

export async function generateCustomerLedgerPdfBlob({
  ledgerData = [],
  customerInfo = {},
  fromDate = "",
  toDate = "",
  entireReport = false,
}) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 595.28 pt
  const pageHeight = doc.internal.pageSize.getHeight(); // 841.89 pt
  const marginX = 24;
  const contentWidth = pageWidth - marginX * 2; // ~547.28 pt

  // Resolve customer details
  const phone = customerInfo?.mobile || customerInfo?.phone || "-";
  const rawName = customerInfo?.name || customerInfo?.fullName || "-";
  const name =
    phone && phone !== "-"
      ? String(rawName).replace(new RegExp(`\\s*\\(${phone}\\)\\s*$`), "").trim() || rawName
      : rawName;

  // Resolve branch & company details
  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile = toEnglishDigits(
    branchDetails.mobile || branchDetails.phone || "-"
  );
  const companyNameUpper = getTenantNameUpper();
  const addressLines = formatBranchAddressLines(branchDetails.address);
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";

  // Calculate totals using Indian comma parsing
  const totals = ledgerData.reduce(
    (acc, row) => {
      const credit =
        typeof row.credit === "string"
          ? parseFloat(row.credit.replace(/,/g, "")) || 0
          : parseFloat(row.credit) || 0;
      const debit =
        typeof row.debit === "string"
          ? parseFloat(row.debit.replace(/,/g, "")) || 0
          : parseFloat(row.debit) || 0;
      return { credit: acc.credit + credit, debit: acc.debit + debit };
    },
    { credit: 0, debit: 0 }
  );

  const lastRow = ledgerData.length > 0 ? ledgerData[ledgerData.length - 1] : null;
  const rawBal = lastRow?.balance ?? 0;
  const finalBalance =
    typeof rawBal === "string"
      ? parseFloat(rawBal.replace(/,/g, "")) || 0
      : parseFloat(rawBal) || 0;

  // Preload top bar & logo image
  const topBarImg = renderTopBarCanvas(contentWidth);
  const logoSrc = getTenantBrandImageSrcWithFallback("sidebar_icon");
  const logoImgData = await loadImageAsDataUrl(logoSrc);

  let currentY = 16;

  // 1. Top religious Gujarati bar
  if (topBarImg) {
    doc.addImage(topBarImg, "PNG", marginX, currentY, contentWidth, 16);
    currentY += 18;
  }

  // 2. Mobile on upper right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(`MO.: ${companyMobile}`, pageWidth - marginX - 6, currentY + 10, { align: "right" });

  // 3. Centered Brand (Logo + Company Name + Address)
  const logoW = 20;
  const logoH = 20;
  doc.setFont("times", "bold");
  doc.setFontSize(17);
  doc.setTextColor(125, 0, 0);
  const compNameW = doc.getTextWidth(companyNameUpper);
  const totalBrandW = logoImgData ? logoW + 6 + compNameW : compNameW;
  const brandStartX = pageWidth / 2 - totalBrandW / 2;

  if (logoImgData) {
    doc.addImage(logoImgData, "PNG", brandStartX, currentY + 1, logoW, logoH);
    doc.text(companyNameUpper, brandStartX + logoW + 6, currentY + 16);
  } else {
    doc.text(companyNameUpper, pageWidth / 2, currentY + 16, { align: "center" });
  }

  // Address
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.text(addressSingleLine, pageWidth / 2, currentY + 28, { align: "center" });

  currentY += 34;

  // Dividing line under header
  doc.setDrawColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.setLineWidth(0.75);
  doc.line(marginX, currentY, pageWidth - marginX, currentY);

  // 4. Red Title Banner: "Customer Ledger Report"
  currentY += 2;
  doc.setFillColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.rect(marginX, currentY, contentWidth, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("Customer Ledger Report", pageWidth / 2, currentY + 12.5, { align: "center" });
  currentY += 21;

  // 5. Customer Info Box (Only on page 1)
  doc.setFillColor(250, 246, 240); // #faf6f0
  doc.setDrawColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.setLineWidth(0.75);
  doc.rect(marginX, currentY, contentWidth, 27, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.text("Name: ", marginX + 6, currentY + 11);
  const nameLabelW = doc.getTextWidth("Name: ");
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(name, marginX + 6 + nameLabelW, currentY + 11);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.text("Phone: ", marginX + 6, currentY + 21);
  const phoneLabelW = doc.getTextWidth("Phone: ");
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(phone, marginX + 6 + phoneLabelW, currentY + 21);

  currentY += 32;

  // 6. Table configuration
  const head = [
    [
      { content: "DATE", styles: { halign: "center" } },
      { content: "TYPE", styles: { halign: "left" } },
      { content: "CREDIT", styles: { halign: "right" } },
      { content: "DEBIT", styles: { halign: "right" } },
      { content: "BALANCE", styles: { halign: "right" } },
      { content: "REMARKS", styles: { halign: "left" } },
    ],
  ];

  const body = ledgerData.map((row) => [
    row.isOpening ? "-" : formatDisplayDate(row.date),
    row.type || (row.isOpening ? "Opening Balance" : "-"),
    row.credit ? formatIndianCurrency(row.credit) : "-",
    row.debit ? formatIndianCurrency(row.debit) : "-",
    row.balance ? formatIndianBalance(row.balance) : "-",
    row.remarks || "",
  ]);

  const foot = [
    [
      "",
      "TOTAL",
      formatIndianCurrency(totals.credit),
      formatIndianCurrency(totals.debit),
      formatIndianBalance(finalBalance),
      "",
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    head,
    body,
    foot,
    showFoot: "lastPage",
    theme: "grid",
    tableWidth: contentWidth,
    margin: { top: 24, left: marginX, right: marginX, bottom: 24 },
    styles: {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 3,
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      font: "helvetica",
      fontSize: 8,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      cellPadding: 3,
    },
    footStyles: {
      font: "helvetica",
      fontSize: 8,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      cellPadding: 3,
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.12, halign: "center" },
      1: { cellWidth: contentWidth * 0.28, halign: "left" },
      2: { cellWidth: contentWidth * 0.12, halign: "right" },
      3: { cellWidth: contentWidth * 0.12, halign: "right" },
      4: { cellWidth: contentWidth * 0.12, halign: "right" },
      5: { cellWidth: contentWidth * 0.24, halign: "left" },
    },
    didParseCell: (data) => {
      // Body rows styling matching exact UI
      if (data.section === "body") {
        const rawRow = ledgerData[data.row.index];
        const isOpening = rawRow?.isOpening || String(rawRow?.type || "").toUpperCase().includes("OPENING BALANCE");

        if (isOpening) {
          data.cell.styles.fillColor = OPENING_BG;
          data.cell.styles.fontStyle = "bold";
        } else {
          // Column-specific soft tints
          if (data.column.index === 2) {
            data.cell.styles.fillColor = CREDIT_BG;
          } else if (data.column.index === 3) {
            data.cell.styles.fillColor = DEBIT_BG;
          } else if (data.column.index === 4) {
            data.cell.styles.fillColor = BALANCE_BG;
          }
        }

        // Single line auto-scaling for number cells (CREDIT, DEBIT, BALANCE)
        if (data.column.index >= 2 && data.column.index <= 4) {
          const rawText = Array.isArray(data.cell.text)
            ? data.cell.text.join(" ")
            : String(data.cell.text ?? "");
          if (rawText && rawText !== "-") {
            const colWidth = contentWidth * 0.12;
            const maxTextWidth = colWidth - 6;
            let fontSize = data.cell.styles.fontSize || 7.5;
            doc.setFont("helvetica", data.cell.styles.fontStyle || "normal");
            doc.setFontSize(fontSize);
            let measuredWidth = doc.getTextWidth(rawText);
            while (measuredWidth > maxTextWidth && fontSize > 4.5) {
              fontSize -= 0.3;
              doc.setFontSize(fontSize);
              measuredWidth = doc.getTextWidth(rawText);
            }
            data.cell.styles.fontSize = fontSize;
            data.cell.styles.overflow = "hidden";
            data.cell.text = [rawText];
          }
        }
      }

      // Foot totals row styling
      if (data.section === "foot") {
        data.cell.styles.fillColor = HEADER_BG;
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [0, 0, 0];
        if (data.column.index === 1) {
          data.cell.styles.halign = "center";
        }
      }
    },
  });

  // Precise page numbers on all pages
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Page ${page} of ${totalPages} (${ledgerData.length} records)`,
      pageWidth - marginX,
      12,
      { align: "right" }
    );
  }

  // Last page: Centered "Generated on: DD-MM-YYYY hh:mm A" with 14pt gap below table
  doc.setPage(totalPages);
  const finalY = doc.lastAutoTable?.finalY || 180;
  const footerY = Math.min(finalY + 14, pageHeight - 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  doc.text(
    `Generated on: ${getFormattedGeneratedOn()}`,
    pageWidth / 2,
    footerY,
    { align: "center" }
  );

  return doc.output("blob");
}
